/**
 * CoachAssignments Component (VH-19)
 *
 * Displays coach-to-user assignments and allows super users
 * to create new assignments and remove existing ones.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { adminAPI } from '../../services/api'

function AddAssignmentForm({ users, onSubmit, isSubmitting }) {
  const { t } = useTranslation()
  const [coachId, setCoachId] = useState('')
  const [userId, setUserId] = useState('')

  const coaches = users.filter((u) => u.role === 'coach')
  const assignableUsers = users.filter((u) => u.id !== Number(coachId))

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!coachId || !userId) return
    onSubmit(Number(coachId), Number(userId))
    setCoachId('')
    setUserId('')
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3 bg-gray-50 p-4 rounded-lg">
      <div className="flex-1 min-w-[200px]">
        <label htmlFor="coach-select" className="block text-sm font-medium text-gray-700 mb-1">
          {t('admin.coachAssignments.coach')}
        </label>
        <select
          id="coach-select"
          value={coachId}
          onChange={(e) => setCoachId(e.target.value)}
          className="block w-full focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
        >
          <option value="">{t('admin.coachAssignments.selectCoach')}</option>
          {coaches.map((coach) => (
            <option key={coach.id} value={coach.id}>
              {coach.email} ({coach.username})
            </option>
          ))}
        </select>
        {coaches.length === 0 && (
          <p className="mt-1 text-xs text-amber-600">{t('admin.coachAssignments.noCoaches')}</p>
        )}
      </div>
      <div className="flex-1 min-w-[200px]">
        <label htmlFor="user-select" className="block text-sm font-medium text-gray-700 mb-1">
          {t('admin.coachAssignments.user')}
        </label>
        <select
          id="user-select"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className="block w-full focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
        >
          <option value="">{t('admin.coachAssignments.selectUser')}</option>
          {assignableUsers.map((user) => (
            <option key={user.id} value={user.id}>
              {user.email} ({user.username})
            </option>
          ))}
        </select>
      </div>
      <button
        type="submit"
        disabled={!coachId || !userId || isSubmitting}
        className="inline-flex items-center gap-1.5 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-on-primary bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <PlusIcon className="h-4 w-4" />
        {t('admin.coachAssignments.addAssignment')}
      </button>
    </form>
  )
}

export default function CoachAssignments() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [removingId, setRemovingId] = useState(null)

  const { data: assignmentsData, isLoading: assignmentsLoading, isError: assignmentsError } = useQuery({
    queryKey: ['admin', 'coach-assignments'],
    queryFn: () => adminAPI.listCoachAssignments({ skip: 0, limit: 100 }),
    select: (response) => response.data,
  })

  const { data: usersData } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => adminAPI.listUsers({ skip: 0, limit: 100 }),
    select: (response) => response.data,
  })

  const createMutation = useMutation({
    mutationFn: ({ coachId, userId }) => adminAPI.createCoachAssignment(coachId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'coach-assignments'] })
      toast.success(t('admin.coachAssignments.assignmentCreated'))
      setShowForm(false)
    },
    onError: (error) => {
      const detail = error.response?.data?.detail || t('admin.coachAssignments.assignmentFailed')
      toast.error(detail)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (assignmentId) => adminAPI.deleteCoachAssignment(assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'coach-assignments'] })
      toast.success(t('admin.coachAssignments.assignmentRemoved'))
      setRemovingId(null)
    },
    onError: () => {
      toast.error(t('admin.coachAssignments.removeFailed'))
      setRemovingId(null)
    },
  })

  const handleCreate = (coachId, userId) => {
    createMutation.mutate({ coachId, userId })
  }

  const handleDelete = (assignment) => {
    const confirmed = window.confirm(t('admin.coachAssignments.confirmRemove'))
    if (confirmed) {
      setRemovingId(assignment.id)
      deleteMutation.mutate(assignment.id)
    }
  }

  if (assignmentsLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-6"></div>
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-12 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    )
  }

  if (assignmentsError) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <p className="ml-3 text-sm text-red-700">{t('common.error')}</p>
        </div>
      </div>
    )
  }

  const assignments = assignmentsData?.assignments || []
  const users = usersData?.users || []

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t('admin.coachAssignments.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('admin.coachAssignments.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-surface-container hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-4 w-4" />
          {t('admin.coachAssignments.addAssignment')}
        </button>
      </div>

      {showForm && (
        <div className="mb-6">
          <AddAssignmentForm
            users={users}
            onSubmit={handleCreate}
            isSubmitting={createMutation.isPending}
          />
        </div>
      )}

      {assignments.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">{t('admin.coachAssignments.noAssignments')}</p>
      ) : (
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                  {t('admin.coachAssignments.coach')}
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  {t('admin.coachAssignments.user')}
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  {t('admin.coachAssignments.assignedAt')}
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-surface-container">
              {assignments.map((assignment) => (
                <tr key={assignment.id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6">
                    {assignment.coach_email}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {assignment.user_email}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {new Date(assignment.created_at).toLocaleDateString()}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <button
                      type="button"
                      onClick={() => handleDelete(assignment)}
                      disabled={removingId === assignment.id}
                      className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <TrashIcon className="h-4 w-4" />
                      {t('admin.coachAssignments.removeAssignment')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {assignmentsData?.total > 0 && (
        <p className="mt-3 text-sm text-gray-500">
          {assignmentsData.total} {assignmentsData.total === 1 ? 'assignment' : 'assignments'}
        </p>
      )}
    </div>
  )
}
